/** Endpoints the platform user directory calls. Backend handoff for this page. */
export const PLATFORM_USER_API = {
  listUsers: 'GET /users',
  createUser: 'POST /users',
  updateUser: 'PUT /users/{id}',
  listMemberships: 'GET /company-instances',
  passwordSetup: 'POST /auth/forgot-password',
  createCompany: 'POST /crud/companies',
  listCompanies: 'GET /crud/companies',
  listTrees: 'GET /artifacts/trees?company_id=',
  listSubscriptionTypes: 'GET /subscriptions/types',
  listFeatures: 'GET /subscriptions/features',
} as const;

export const ACCOUNT_MERGE_REFUSAL =
  'Account merge is not sent from this page. It waits on a krysalis_central audit path.';

export const COMPANY_MERGE_REFUSAL =
  'Company merge is not sent from this page. It waits on a krysalis_central audit path.';

export const CATALOG_WRITE_REFUSAL =
  'Subscription catalog changes are not written from this page. Mutations wait on krysalis_central approval. Company plans stay on /subscription-management.';

export function refuseCatalogWrite(): Error {
  return new Error(CATALOG_WRITE_REFUSAL);
}

export interface DirectoryUser {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  role?: string;
}

export interface MembershipRef {
  companyId: string;
  companyName: string;
}

export function filterPlatformUsers<T extends DirectoryUser>(
  users: T[],
  memberships: Record<string, MembershipRef[]>,
  query: string,
  companyId?: string,
): T[] {
  const needle = query.trim().toLowerCase();
  const company = String(companyId || '').trim();
  return users.filter((user) => {
    const companies = memberships[user.id] || [];
    if (company && !companies.some((row) => row.companyId === company)) return false;
    if (!needle) return true;
    const hay = [user.username, user.email, user.full_name, user.role, ...companies.map((row) => row.companyName)]
      .join(' ')
      .toLowerCase();
    return hay.includes(needle);
  });
}

/** Same normalized company name on more than one company id. */
export function duplicateCompanyNameGroups(
  companies: Array<{ id: string; name?: string }>,
): Array<{ name: string; ids: string[] }> {
  const byName = new Map<string, string[]>();
  for (const company of companies) {
    const name = String(company.name || '').trim().toLowerCase();
    if (!name) continue;
    const ids = byName.get(name) || [];
    ids.push(company.id);
    byName.set(name, ids);
  }
  return [...byName.entries()]
    .filter(([, ids]) => ids.length > 1)
    .map(([name, ids]) => ({ name, ids }));
}

/** Same normalized email on more than one user id. */
export function duplicateEmailGroups(users: DirectoryUser[]): Array<{ email: string; ids: string[] }> {
  const byEmail = new Map<string, string[]>();
  for (const user of users) {
    const email = user.email.trim().toLowerCase();
    if (!email) continue;
    const ids = byEmail.get(email) || [];
    ids.push(user.id);
    byEmail.set(email, ids);
  }
  return [...byEmail.entries()]
    .filter(([, ids]) => ids.length > 1)
    .map(([email, ids]) => ({ email, ids }));
}

export interface ProductModelNode {
  id: string;
  name: string;
  children: ProductModelNode[];
}

function parentModelId(row: {
  parent_id?: string | null;
  parent_tree_id?: string | null;
  metadata?: Record<string, unknown> | null;
}): string | null {
  const metadata = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
  const raw = row.parent_id || row.parent_tree_id || metadata.parent_tree_id || metadata.parent_id;
  const parentId = String(raw || '').trim();
  return parentId || null;
}

export function nestProductModels(
  rows: Array<{
    id?: string;
    name?: string;
    parent_id?: string | null;
    parent_tree_id?: string | null;
    metadata?: Record<string, unknown> | null;
  }>,
): ProductModelNode[] {
  const nodes = new Map<string, ProductModelNode & { parentId: string | null }>();
  for (const row of rows) {
    const id = String(row.id || '').trim();
    if (!id) continue;
    nodes.set(id, {
      id,
      name: String(row.name || id),
      parentId: parentModelId(row),
      children: [],
    });
  }
  const roots: ProductModelNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent && parent.id !== node.id) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}
